import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { Tabs } from '../layout';
import styles from './CanvassInfo.module.css';
import { Container } from '../layout';
import { Button } from '../layout/buttons';
import { Modal } from '../layout/Modal';
import CanvassAssistantChat from './CanvassAssistantChat';
import { config } from '../../config/environment';
import Icon from '../layout/icons/Icons';
import canvassStyles from './Canvass.module.css';
import PreviewIntroductions from './tabs/PreviewIntroductions';
import DetailsSections from './tabs/DetailsSections';
import ObjectivesSection from './tabs/ObjectivesSection';
import ReportSection from './tabs/ReportSection';
import CanvassInsightsPDFDocument from '../CanvassInsightsPDFDocument';
import { pdf } from '@react-pdf/renderer';
import { completeCanvass, activateCanvass } from '../../utils/canvasses';
import { logger } from '../../utils/logger';

const CanvassInfo = ({ initialExtractedInsights = null, onSaveIntroductions, onSaveCanvassPatch, canvass, onActiveTabChange, clientId: clientIdProp, displayNameProp: displayNameProp, canvassName: canvassNameProp, canvassBrief: canvassBriefProp, context, interviewStarted, onInterviewStart }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const hasReadSessionRef = useRef(false);
    const chatRef = useRef(null);

    // Initialize sessionState synchronously from sessionStorage to prevent undefined on initial render
    const getInitialSessionState = () => {
        try {
            const sessionStateStr = sessionStorage.getItem('newCanvassState');
            if (sessionStateStr) {
                const parsed = JSON.parse(sessionStateStr);
                sessionStorage.removeItem('newCanvassState'); // Clean up immediately
                return parsed;
            }
        } catch (e) {
            console.error('Failed to parse sessionStorage state:', e);
        }
        return {};
    };

    const [sessionState, setSessionState] = useState(getInitialSessionState);

    useEffect(() => {
        // Mark as read to prevent re-reading on re-renders
        hasReadSessionRef.current = true;
    }, []);

    // Use props first, then sessionState, then canvass object, then defaults
    const clientId = clientIdProp || sessionState.clientId || null;
    const displayName = displayNameProp || sessionState.displayName || null;
    const canvassName = canvassNameProp || canvass?.canvassName || sessionState.title || 'New Canvass';
    const canvassBrief = canvassBriefProp || canvass?.canvassBrief || sessionState.canvassBrief || '';
    const canvassContext = context || sessionState.context || null;
    const [activeTab, setActiveTab] = useState(() => (
        ['active', 'complete'].includes(String(canvass?.status || '')) ? 'results' : 'overview'
    ));
    const [extractedInsights, setExtractedInsights] = useState(initialExtractedInsights);
    const [pills, setPills] = useState(null);
    const [extractedInfo, setExtractedInfo] = useState([]);
    const [groupedInsights, setGroupedInsights] = useState({});
    const [isUpdating, setIsUpdating] = useState(false);
    const [showActivateModal, setShowActivateModal] = useState(false);
    const [isActivating, setIsActivating] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [isCompleting, setIsCompleting] = useState(false);
    const [tabsCollapsed, setTabsCollapsed] = useState(false);
    const [isTerminating, setIsTerminating] = useState(false);
    const randomInititalIntroduction = () => {
        const introductions = [
            "Hi—let’s begin. Every canvass starts with a spark. What’s motivating you right now—a challenge, decision, or change you’d like to see more clearly?",
        
            "Let’s get started. Behind every canvass is a reason. What prompted you to open this one—an issue you’re working through, a choice ahead, or a change you’d like to guide?",
        
            "Welcome. A canvass is a chance to pause and reflect. What’s on your mind today that you’d like to explore—something you’re navigating, deciding, or hoping to shift?",
        ];
    
        return introductions[Math.floor(Math.random() * introductions.length)];
    };
    // Modal for introductions moved to PreviewIntroductions

    // Helpers copied from ResearchInsights.jsx
    const formatKeyName = (key) => {
        if (key.startsWith('sq_conv_')) {
            const match = key.match(/_(\d+)$/);
            if (match) return `Question ${parseInt(match[1]) + 1}`;
            return 'Question';
        }
        return key
            .replace(/_\d+$/, '')
            .split('_')
            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
    };

    const parseValue = (value) => {
        if (!value) return value;
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'string' && value.startsWith('{') && value.endsWith('}')) {
            try {
                const parsed = JSON.parse(value);
                if (parsed.question) return { type: 'seed_question', data: parsed };
            } catch {}
        }
        // Handle object-shaped values (e.g., main_objective as an object)
        if (typeof value === 'object' && value !== null) {
            // Common shape: { emotion: string, behaviour: string }
            const emotion = value.emotion || value.emotions;
            const behaviour = value.behaviour || value.behavior;
            if (emotion || behaviour) {
                // Prefer behaviour as the main sentence; append emotion inline
                const parts = [];
                if (typeof behaviour === 'string') parts.push(behaviour);
                if (typeof emotion === 'string') parts.push(`Emotion: ${emotion}`);
                return parts.join(' — ');
            }
            // Fallback: stringify simple objects safely
            try {
                return JSON.stringify(value);
            } catch {
                return String(value);
            }
        }
        if (typeof value === 'string' && value.includes(',') && value.includes('/')) {
            return { type: 'tag_list', data: value.split(',').map(s => s.trim()) };
        }
        return value;
    };

    const hasAnyInsights = () => Object.keys(groupedInsights).length > 0;

    const runtimeUrl = useMemo(() => {
        const baseUrl = config.discoveryApiBase + '/chat';
        const url = new URL(baseUrl);
        url.searchParams.append('environment', config.environment);
        url.searchParams.append('frontend_url', config.frontendUrl);
        return url.toString();
    }, []);

    // Parent no longer loads onboarding interview; responsibility moved to CanvassAssistantChat.

    // Show a Preview tab when we have interview introductions to present
    const hasPreview = !!(extractedInsights?.interview_introductions && extractedInsights.interview_introductions.length > 0);

    useEffect(() => {
        // Preload extracted insights if provided by parent
        if (initialExtractedInsights) {
            setExtractedInsights(initialExtractedInsights);
        }
    }, [initialExtractedInsights]);

    // If canvass becomes active or complete, switch to results tab
    useEffect(() => {
        const status = String(canvass?.status || '');
        if (['active', 'complete'].includes(status) && activeTab !== 'results') {
            setActiveTab('results');
        }
    }, [canvass?.status]);

    // React to extractedInsights updates to update local extractedInfo and grouping
    useEffect(() => {
        if (extractedInsights?.extractedInfo && extractedInsights.extractedInfo.length > 0) {
            const filteredInfo = extractedInsights.extractedInfo.filter(item => {
                if (!item.value) return false;
                if (Array.isArray(item.value)) return item.value.length > 0;
                const lowercaseValue = String(item.value).toLowerCase().trim();
                return !(
                    lowercaseValue === 'unknown' ||
                    lowercaseValue === 'unknown company' ||
                    lowercaseValue === 'client' ||
                    lowercaseValue === 'not specified' ||
                    lowercaseValue === 'n/a' ||
                    lowercaseValue === ''
                );
            });
            setExtractedInfo(filteredInfo);
            setIsUpdating(true);
            setTimeout(() => setIsUpdating(false), 800);
        }
    }, [extractedInsights]);

    useEffect(() => {
        const grouped = {};
        extractedInfo.forEach(item => {
            item.tags.forEach(tag => {
                if (!grouped[tag]) grouped[tag] = [];
                grouped[tag].push({ key: item.key, value: item.value, formattedKey: formatKeyName(item.key) });
            });
        });
        setGroupedInsights(grouped);
    }, [extractedInfo]);

        // Export the current canvass extracted insights as a PDF
    const exportCanvassToPDF = async () => {
        try {
            const cv = canvass || {};
            const tagWhitelist = ['general_info', 'audience_profile', 'context'];
        
            // Build PDF document
            const doc = (
                <CanvassInsightsPDFDocument
                canvass={cv}
                groupedInsights={groupedInsights}
                tagWhitelist={tagWhitelist}
                />
            );
            const asPdf = pdf(doc);
            const blob = await asPdf.toBlob();
        
            // Filename: <CanvassName>_Extracted_Insights_<YYYYMMDD_HHMM>_<CanvassID>.pdf
            const pad = (n) => String(n).padStart(2, '0');
            const d = new Date();
            const ts = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
            const safeName = (cv?.canvassName || 'Canvass').toString().replace(/[^a-z0-9_-]+/gi, '_');
            const idPart = cv?.canvassId || cv?.id || 'unknown';
            const filename = `${safeName}_Extracted_Insights_${ts}_${idPart}.pdf`;
        
            // Trigger download
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            logger.error('Error exporting canvass to PDF:', err);
        }
    };

    // Status helpers
    const statusStr = String(canvass?.status || '');
    // General editability: only when canvass is in progress or draft
    const isEditable = ['draft'].includes(statusStr);
    // Introductions remain editable through 'approved', but not 'active'
    const isIntroductionsEditable = ['draft', 'approved'].includes(statusStr);

    const tabsToggleButton = statusStr === 'in_progress' ? (
        <Button
            variant="outline-neutral"
            size="sm"
            iconOnly
            ariaLabel={tabsCollapsed ? 'Show tabs' : 'Hide tabs'}
            iconLeft={{ name: tabsCollapsed ? 'arrows/keyboard_double_arrow_right' : 'arrows/keyboard_double_arrow_left' }}
            onClick={() => setTabsCollapsed(v => !v)}
        />
    ) : null;

    return (
        <Container fluid className={styles.dataContainer}>
            <Row>
                <Col md={statusStr === 'in_progress' ? (tabsCollapsed ? 1 : 6) : 8}>
                    {tabsCollapsed && tabsToggleButton && (
                        <div className={styles.chatToggleRowCollapsed}>
                            {tabsToggleButton}
                        </div>
                    )}
                    {!tabsCollapsed && (
                    <>
                    {hasAnyInsights() ? (
                        <Tabs
                            size="md"
                            activeKey={activeTab}
                            onChange={(key) => { setActiveTab(key); if (typeof onActiveTabChange === 'function') onActiveTabChange(key); }}
                            items={[
                                { height: '24px', width: '24px', key: 'overview', text: 'Overview' },
                                { height: '24px', width: '24px', key: 'details', text: 'Details' },
                                ...(hasPreview ? [{ height: '24px', width: '24px', key: 'preview', text: 'Canvass Preview' }] : []),
                                ...(['active', 'complete'].includes(statusStr) ? [{ height: '24px', width: '24px', key: 'results', text: 'Results' }] : [])
                            ]}
                            className={styles.canvassTabs}
                            trailing={tabsToggleButton}
                        >
                            {activeTab === 'overview' && (
                                <div>
                                    <ObjectivesSection
                                        groupedInsights={groupedInsights}
                                        onSaveCanvassPatch={onSaveCanvassPatch}
                                        isEditable={isEditable}
                                    />
                                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
                                        <Button
                                            className={styles.downloadBtn}
                                            variant="outline-neutral"
                                            iconLeft={{ name: 'arrows/arrow_downward' }}
                                            text="Download"
                                            onClick={exportCanvassToPDF}
                                        >
                                            Download
                                        </Button>
                                        {canvass?.status === 'in_progress' && !isTerminating && (
                                            <Button
                                                className={styles.downloadBtn}
                                                variant="primary"
                                                text="Build the Canvass"
                                                onClick={() => chatRef.current?.sendBuildMessage?.()}
                                            >
                                                Build the Canvass
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                            {activeTab === 'details' && (
                                <div>
                                    <DetailsSections
                                        extractedInsights={extractedInsights}
                                        groupedInsights={groupedInsights}
                                        onSaveCanvassPatch={onSaveCanvassPatch}
                                        isEditable={isEditable}
                                    />
                                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
                                        <Button
                                            className={styles.downloadBtn}
                                            variant="outline-neutral"
                                            iconLeft={{ name: 'arrows/arrow_downward' }}
                                            text="Download"
                                            onClick={exportCanvassToPDF}
                                        >
                                            Download
                                        </Button>
                                        {canvass?.status === 'in_progress' && !isTerminating && (
                                            <Button
                                                className={styles.downloadBtn}
                                                variant="primary"
                                                text="Build the Canvass"
                                                onClick={() => chatRef.current?.sendBuildMessage?.()}
                                            >
                                                Build the Canvass
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                            {activeTab === 'preview' && hasPreview && (
                                <div>
                                    <PreviewIntroductions
                                        extractedInsights={extractedInsights}
                                        onLocalUpdateIntroductions={(updated) =>
                                            setExtractedInsights(prev => ({ ...(prev || {}), interview_introductions: updated }))
                                        }
                                        onSaveIntroductions={onSaveIntroductions}
                                        onSaveCanvassPatch={onSaveCanvassPatch}
                                        canvassConstraints={canvass?.constraints}
                                        strategyOutline={canvass?.strategyOutline}
                                        preambleQuestions={canvass?.preamble_questions}
                                        interviewExperience={canvass?.interviewExperience}
                                        closingMessage={canvass?.closingMessage}
                                        isEditable={isIntroductionsEditable}
                                    />

                                    {/* {extractedInsights.interviewLink && (
                                        <div className={styles.section + ' ' + styles.buttonEnd}>
                                            <Button
                                                className={styles.downloadBtn}
                                                variant="outline-neutral"
                                                iconLeft={{ name: 'arrows/arrow_outward' }}
                                                text="Open Interview"
                                                onClick={() => window.open(extractedInsights.interviewLink, '_blank', 'noopener,noreferrer')}
                                            >
                                                Open Interview
                                            </Button>
                                        </div>
                                    )} */}
                                </div>
                            )}
                            {activeTab === 'results' && (['active', 'complete'].includes(statusStr)) && (
                                <div>
                                    <ReportSection canvass={canvass} />
                                </div>
                            )}
                        
                        </Tabs>
                    ) : (
                        <div>
                            {tabsToggleButton && (
                                <div className={styles.chatToggleRow}>
                                    {tabsToggleButton}
                                </div>
                            )}
                            <div className={styles.section} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column' }}>
                                <Icon name="augustsays/Beep-colour" width={105.561} height={120} />
                                <div style={{ marginTop: 12, fontSize: 18, fontWeight: 600 }}>Let's get to work</div>
                                <div style={{ marginTop: 6, color: 'var(--text-secondary, #6c757d)' }}>Use the chat to build your Canvass</div>
                            </div>
                        </div>
                    )}
                    </>
                    )}
                </Col>
                <Col md={statusStr === 'in_progress' ? (tabsCollapsed ? 11 : 6) : 4} style={{ background: 'var(--brand-as-blue-050)' }}>
                    <aside className={styles.sidebar}>
                        <div>
                            {(canvass?.status === 'complete') && (
                                <div className={styles.section + ' ' + styles.draftSection}>
                                    <div className={styles.beepSection}>
                                        <Icon name="augustsays/Bop" width={175} height={175} style={{ display: 'block', margin: 'auto' }} />
                                    </div>
                                    <div className={styles.draftMessage}>
                                        <div className={styles.draftMessageTitle}>Canvass Complete</div>
                                        <div className={styles.draftMessageSub}>Information about the Canvass can be found in the Results tab.</div>
                                    </div>
                                    <div className={styles.draftButtons}>
                                        <Button
                                            className={styles.draftButton}
                                            variant="outline-neutral"
                                            text="Results"
                                            style={{ width: '100%' }}
                                            onClick={() => setActiveTab('results')}
                                        >
                                            Results
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {canvass?.status === 'active' && (
                                <div className={styles.section + ' ' + styles.draftSection}>
                                    <div className={styles.beepSection}>
                                        <Icon name="augustsays/Boop-colour2" width={175} height={175} style={{ display: 'block', margin: 'auto' }} />
                                    </div>
                                    <div className={styles.draftMessage}>
                                        <div className={styles.draftMessageTitle}>Active</div>
                                        <div className={styles.draftMessageSub}>This Canvass is currently live. You can share or revisit the Interview link anytime.</div>
                                    </div>
                                    <div className={styles.draftButtons}>
                                        <Button
                                            className={styles.draftButton}
                                            variant="primary"
                                            text="Complete Canvass"
                                            style={{ width: '100%' }}
                                            onClick={() => setShowCompleteModal(true)}
                                        >
                                            Complete Canvass
                                        </Button>
                                        <Button
                                            className={styles.draftButton}
                                            variant="outline-neutral"
                                            text="View Interview"
                                            style={{ width: '100%' }}
                                            onClick={() => canvass?.interviewLink && window.open(canvass.interviewLink, '_blank', 'noopener,noreferrer')}
                                        >
                                            View Interview
                                        </Button>
                                        <Button
                                            className={styles.draftButton}
                                            variant="outline-neutral"
                                            text="Copy Link"
                                            style={{ width: '100%' }}
                                            onClick={() => {
                                                if (!canvass?.interviewLink) return;
                                                if (navigator.clipboard && navigator.clipboard.writeText) {
                                                    navigator.clipboard.writeText(canvass.interviewLink).catch(() => {
                                                        window.prompt('Copy this interview link:', canvass.interviewLink);
                                                    });
                                                } else {
                                                    window.prompt('Copy this interview link:', canvass.interviewLink);
                                                }
                                            }}
                                        >
                                            Copy Link
                                            <Icon name="arrows/cached" width={18} height={18} style={{ marginLeft: 6 }} />
                                        </Button>
                                    </div>
                                    {canvass?.interviewLink && (
                                        <div className={styles.section} style={{ marginTop: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                                            <img
                                                id="canvass-qr-img"
                                                src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(canvass.interviewLink)}`}
                                                alt="Canvass QR Code"
                                                width={240}
                                                height={240}
                                                style={{ borderRadius: 8, background: '#fff' }}
                                            />
                                            <div>
                                                <Button
                                                    variant="outline-neutral"
                                                    text="Download QR"
                                                    onClick={() => {
                                                        try {
                                                            const img = document.getElementById('canvass-qr-img');
                                                            if (img && img.src) {
                                                                window.open(img.src, '_blank', 'noopener,noreferrer');
                                                            }
                                                        } catch {}
                                                    }}
                                                >
                                                    Download QR
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {canvass?.status === 'draft' && (
                                <div className={styles.section + ' ' + styles.draftSection}>
                                    <div className={styles.boopSection}>
                                        <Icon name="augustsays/boop" width={175} height={175} style={{ display: 'block', margin: 'auto' }} />
                                    </div>
                                    <div className={styles.draftMessage}>
                                        <div className={styles.draftMessageTitle}>Draft.</div>
                                        <div className={styles.draftMessageSub}>Approve your canvass to finalize before it goes live.</div>
                                    </div>
                                    <div className={styles.draftButtons}>
                                        <Button
                                            className={styles.draftButton}
                                            variant="primary"
                                            text="Approve"
                                            style={{ width: '100%' }}
                                            disabled={!canvass?.strategyOutline}
                                            onClick={() => setShowApproveModal(true)}
                                        >
                                            Approve
                                        </Button>
                                        {/* <Button
                                            className={styles.draftButton}
                                            variant="outline-neutral"
                                            text="Preview"
                                            style={{ width: '100%' }}
                                            onClick={() => canvass?.interviewLink
                                                ? window.open(canvass.interviewLink, '_blank', 'noopener,noreferrer')
                                                : setActiveTab('preview')}
                                        >
                                            Preview Interview
                                        </Button> */}
                                    </div>
                                </div>
                            )}
                            {canvass?.status === 'approved' && (
                                <div className={styles.section + ' ' + styles.draftSection}>
                                    <div className={styles.beepSection}>
                                        <Icon name="augustsays/Boop-colour2" width={175} height={175} style={{ display: 'block', margin: 'auto' }} />
                                    </div>
                                    <div className={styles.draftMessage}>
                                        <div className={styles.draftMessageTitle}>Approved.</div>
                                        <div className={styles.draftMessageSub}>Data will be captured from live participants while the interview is active.</div>
                                    </div>
                                    <div className={styles.draftButtons}>
                                        <Button
                                            className={styles.draftButton}
                                            variant="primary"
                                            text="Activate now"
                                            style={{ width: '100%' }}
                                            onClick={() => setShowActivateModal(true)}
                                        >
                                            Activate now
                                        </Button>
                                        <Button
                                            className={styles.draftButton}
                                            variant="outline-neutral"
                                            text="Preview"
                                            style={{ width: '100%' }}
                                            onClick={() => canvass?.interviewLink
                                                ? window.open(canvass.interviewLink, '_blank', 'noopener,noreferrer')
                                                : setActiveTab('preview')}
                                        >
                                            Preview Interview
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {canvass?.status === 'in_progress' && (
                                <CanvassAssistantChat
                                    ref={chatRef}
                                    runtimeUrl={ runtimeUrl }
                                    initialText={ randomInititalIntroduction() }
                                    clientId={ clientId }
                                    displayName={ displayName }
                                    canvass={ canvass }
                                    canvassInfo={{ canvassName, canvassBrief }}
                                    context={canvassContext}
                                    interviewStarted={interviewStarted}
                                    onInterviewStart={onInterviewStart}
                                    onPills={ setPills }
                                    onConversationId={(cid) => setExtractedInsights(prev => ({ ...(prev || {}), conversationId: cid }))}
                                    onExtractedInfo={(extractedInfoArray) => {
                                        setExtractedInsights(prev => {
                                            const existingInfo = prev?.extractedInfo || [];
                                            const merged = [...existingInfo];
                                            extractedInfoArray.forEach(newItem => {
                                                const idx = merged.findIndex(it => it.key === newItem.key);
                                                if (idx >= 0) merged[idx] = newItem; else merged.push(newItem);
                                            });
                                            return { ...(prev || {}), extractedInfo: merged, extractedAt: new Date().toLocaleString() };
                                        });
                                    }}
                                    onShouldTerminate={(convId) => {
                                        // Record the conversation id
                                        setExtractedInsights(prev => ({ ...(prev || {}), conversationId: convId || prev?.conversationId }));
                                        // Hard reload to reflect completion state and switch UI accordingly
                                        if (convId) {
                                            try { window.location.replace(`${config.frontendUrl}/canvass/${convId}`); } catch {}
                                        }
                                    }}
                                    onTerminating={() => setIsTerminating(true)}
                                />
                            )}
                        </div>
                    </aside>
                </Col>

            </Row>

            {/* Preview Introductions moved to separate component */}
            {/* Approve Canvass confirmation modal */}
            <Modal
                open={showApproveModal}
                onClose={() => !isApproving && setShowApproveModal(false)}
                title="Approve Canvass"
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
                        <Button variant="outline-neutral" disabled={isApproving} onClick={() => setShowApproveModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            disabled={isApproving}
                            onClick={async () => {
                                try {
                                    setIsApproving(true);
                                    if (typeof onSaveCanvassPatch === 'function') {
                                        await onSaveCanvassPatch({ status: 'approved' });
                                    }
                                    setShowApproveModal(false);
                                } catch (e) {
                                    logger.error('Failed to approve canvass:', e);
                                } finally {
                                    setIsApproving(false);
                                }
                            }}
                        >
                            {isApproving ? 'Approving…' : 'Approve'}
                        </Button>
                    </div>
                }
            >
                <div style={{ color: 'var(--text-secondary, #6c757d)' }}>
                    You’re about to approve this canvass — the point where ideas become action. Once approved, details can’t be changed. Ready to make it official?
                </div>
            </Modal>

            {/* Activate Canvass confirmation modal */}
            <Modal
                open={showActivateModal}
                onClose={() => !isActivating && setShowActivateModal(false)}
                title="Activate Canvass"
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
                        <Button variant="outline-neutral" disabled={isActivating} onClick={() => setShowActivateModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            disabled={isActivating}
                            onClick={async () => {
                                try {
                                    setIsActivating(true);
                                    const id = canvass?.canvassId || canvass?.id;
                                    if (id) {
                                        await activateCanvass(id);
                                    } else if (typeof onSaveCanvassPatch === 'function') {
                                        await onSaveCanvassPatch({ status: 'active' });
                                    }
                                    setShowActivateModal(false);
                                    try { window.location.reload(); } catch {}
                                } catch (e) {
                                    logger.error('Failed to activate canvass:', e);
                                } finally {
                                    setIsActivating(false);
                                }
                            }}
                        >
                            {isActivating ? 'Activating…' : 'Activate'}
                        </Button>
                    </div>
                }
            >
                <div style={{ color: 'var(--text-secondary, #6c757d)' }}>
                    Data will be captured from live participants while the interview is active. You can share the link once activated.
                </div>
            </Modal>

            {/* Complete Canvass confirmation modal */}
            <Modal
                open={showCompleteModal}
                onClose={() => !isCompleting && setShowCompleteModal(false)}
                title="Complete Canvass"
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
                        <Button variant="outline-neutral" disabled={isCompleting} onClick={() => setShowCompleteModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            disabled={isCompleting}
                            onClick={() => {
                                setIsCompleting(true);
                                setShowCompleteModal(false);
                                const id = canvass?.canvassId || canvass?.id;

                                const onDone = () => {
                                    try { window.location.reload(); } catch {}
                                };
                                const onErr = (e) => {
                                    logger.error('Failed to complete canvass:', e);
                                };
                                const onFinally = () => {
                                    setIsCompleting(false);
                                };

                                if (id) {
                                    completeCanvass(id).then(onDone).catch(onErr).finally(onFinally);
                                    return;
                                }

                                if (typeof onSaveCanvassPatch === 'function') {
                                    onSaveCanvassPatch({ status: 'complete' }).then(onDone).catch(onErr).finally(onFinally);
                                    return;
                                }

                                setIsCompleting(false);
                            }}
                        >
                            {isCompleting ? 'Completing…' : 'Complete'}
                        </Button>
                    </div>
                }
            >
                <div style={{ color: 'var(--text-secondary, #6c757d)' }}>
                    You’re closing this canvass and wrapping up insights. Once complete, responses will be archived and results locked for viewing only. Would you like to mark this canvass as complete?
                </div>
            </Modal>

        </Container>
    );
};

export default CanvassInfo;
